package dev.lingvo.site.components.studio.compose

import dev.lingvo.shared.CoverageDto
import dev.lingvo.shared.CoverageState
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.RenderDto
import dev.lingvo.shared.RenderCellRef
import dev.lingvo.shared.RenderCheckReportDto
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.ScreenshotSpecs
import dev.lingvo.shared.ShotPurpose
import dev.lingvo.shared.SizeRule
import dev.lingvo.shared.screenshots.ScreenshotEngine
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.render.effectiveLayers
import dev.lingvo.shared.screenshots.render.primaryCaptionLayer
import dev.lingvo.shared.screenshots.text.FitResult
import dev.lingvo.shared.screenshots.text.Script

/** Caption identity uses stable slot keys rather than reorderable positions. */
data class CaptionCell(val slotKey: String, val locale: String)

/** The exact stored value and its UTF-8 hash travel together into the render plan. */
data class ComposeCaption(val text: String, val sha256: String, val fit: FitResult? = null)

/** A job pins the inputs that made it necessary; uploads never inherit a newer capture silently. */
data class ComposeJob(
    val slotId: String, val slotKey: String, val title: String, val position: Int,
    val locale: String, val device: DeviceClass, val captureId: String?,
    val caption: ComposeCaption, val designVersion: Int, val width: Int, val height: Int,
    val boardId: String? = null, val captureSha256: String? = null,
    val primaryCaptionId: String? = null, val schemaVersion: Int = 1,
) {
    /** Refused floor fits remain visible in progress rather than vanishing from the plan. */
    val refused: FitResult.Refused? get() = caption.fit as? FitResult.Refused
}

/** Effective visible image sources decide whether capture provenance is required; legacy plans still require it. */
fun planCompose(
    plan: ScreenshotPlanDto, coverage: CoverageDto, designVersion: Int, renders: List<RenderDto>,
    captions: Map<CaptionCell, ComposeCaption>, design: ScreenshotDesign? = null,
    captureHashes: Map<String, String> = emptyMap(),
): List<ComposeJob> {
    require(plan.platform == coverage.platform) { "Coverage must belong to this platform" }
    val source = coverage.locales.firstOrNull { it.isSource }?.locale
    val currentRenders = renders.associateBy { Triple(it.slotId, it.locale, it.deviceClass) }
    return plan.slots.flatMap { slot ->
        val row = coverage.rows.firstOrNull { it.slotId == slot.id }
        coverage.locales.flatMap { language ->
            slot.devices.mapNotNull { device ->
                val board = design?.boards?.firstOrNull { it.deviceClass == device.id && it.pages.any { p -> slot.key in p.slotIds } }
                if (design != null && board == null) return@mapNotNull null
                val page = board?.pages?.firstOrNull { slot.key in it.slotIds }
                val schemaVersion = design?.schemaVersion ?: 1
                val effective = if (page != null && board != null) effectiveLayers(page, board, language.locale, schemaVersion) else emptyList()
                val primary = if (schemaVersion >= 3 && page != null) primaryCaptionLayer(page, effective, page.slotIds.indexOf(slot.key)) else null
                val captionKey = if (schemaVersion >= 3) when (val content = primary?.content) {
                    is TextContent.Key -> content.key
                    is TextContent.SlotCaption -> page?.slotIds?.getOrNull(content.slot)
                    else -> null
                } else slot.key
                val caption = if (schemaVersion >= 3 && primary == null) ComposeCaption("", "")
                    else captions[CaptionCell(captionKey ?: return@mapNotNull null, language.locale)] ?: return@mapNotNull null
                val expectedCaptionHash = if (schemaVersion >= 3 && primary == null) null else caption.sha256
                val needsCapture = board == null || page == null || effective.any(::usesCapture)
                val capture = if (needsCapture) {
                    val captureLocale = if (slot.localizedUi) language.locale else source ?: return@mapNotNull null
                    row?.cells?.firstOrNull { it.locale == captureLocale && it.deviceClass == device }
                        ?.takeIf { it.state == CoverageState.CURRENT && !it.blobMissing && it.captureId != null } ?: return@mapNotNull null
                } else null
                val captureId = capture?.captureId
                val captureHash = captureId?.let { captureHashes[it] }
                val old = currentRenders[Triple(slot.id, language.locale, device)]
                val sameCaptureHash = if (captureId == null) old?.captureSha256 == null
                    else captureHash == null || old?.captureSha256 == captureHash
                if (old != null && old.designVersion == designVersion && old.captionSha256 == expectedCaptionHash &&
                    old.captureId == captureId && sameCaptureHash) return@mapNotNull null
                val size = (ScreenshotSpecs.rule(device, ShotPurpose.STORE) as? SizeRule.Exact)?.sizes?.firstOrNull()
                ComposeJob(slot.id, slot.key, slot.title, slot.position, language.locale, device, captureId, caption,
                    designVersion, board?.canvas?.width ?: size?.width ?: 1080,
                    board?.canvas?.height ?: size?.height ?: 1920, board?.id, captureHash, primary?.base?.id, schemaVersion)
            }
        }
    }.sortedWith(compareBy<ComposeJob> { scriptGroup(it.locale, it.caption.text) }
        .thenBy { it.locale }.thenBy { it.position }.thenBy { it.slotKey }.thenBy { it.device.id })
}

private fun usesCapture(layer: Layer): Boolean = !layer.base.hidden && layer.base.opacity != 0.0 && when (layer) {
    is DeviceLayer -> layer.screen is ImageSource.Capture
    is ImageLayer -> layer.source is ImageSource.Capture
    is GroupLayer -> layer.children.any(::usesCapture)
    else -> false
}

private fun scriptGroup(locale: String, text: String): Int = when (ScreenshotEngine.scripts.scriptFor(locale, text)) {
    Script.LATIN, Script.VIETNAMESE -> 0
    Script.CYRILLIC, Script.GREEK -> 1
    Script.HANS, Script.HANT, Script.HK, Script.JAPANESE, Script.KOREAN -> 2
    Script.ARABIC, Script.URDU, Script.HEBREW -> 3
    Script.DEVANAGARI, Script.BENGALI, Script.GUJARATI, Script.GURMUKHI, Script.KANNADA,
    Script.MALAYALAM, Script.TAMIL, Script.TELUGU -> 4
    Script.THAI, Script.KHMER, Script.MYANMAR -> 5
}

/** Counts the check snapshot together with work completed since that snapshot. */
fun composedCellCount(report: RenderCheckReportDto, jobs: List<ComposeJob>, completed: Set<RenderCellRef>): Int {
    val pending = jobs.map { RenderCellRef(it.slotKey, it.device, it.locale) }.toSet()
    val stored = report.renders.count { RenderCellRef(it.slotKey, it.deviceClass, it.locale) !in pending }
    return (stored + completed.count { it in pending }).coerceAtMost(report.renders.size + report.missing.size)
}
